import { t } from '@lingui/core/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NavioOverlayActions, useAppForm } from 'noddi-ui-web';
import { useAuthContext } from 'noddi-web';
import { z } from 'zod';

const getNameSchema = () =>
  z.object({
    firstName: z
      .string()
      .trim()
      .min(1, t`First name is required`),
    // Only presence is checked here. What counts as a real name is the backend's
    // call, and the data-quality recheck keeps the step open until it agrees.
    lastName: z
      .string()
      .trim()
      .min(1, t`Last name is required`)
  });

type FormValues = z.input<ReturnType<typeof getNameSchema>>;

const FORM_ID = 'name-update-form';

/** Inner name-update form (first/last name on the signed-in user), embeddable without dialog chrome. */
export const NameUpdateFields = ({ onSaved, saveLabel }: { onSaved: () => void; saveLabel?: string }) => {
  const { userData, updateUserData } = useAuthContext();
  const user = userData?.user;

  const { mutate: updateName, isPending } = noddiAsync.usePatch({
    type: URLKeys.patchAccount,
    queryConfig: {
      onSuccess: async ({ data }) => {
        if (userData) {
          updateUserData({ ...userData, user: data });
        }
        onSaved();
      }
    }
  });

  const form = useAppForm({
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? ''
    } satisfies FormValues,
    validators: { onSubmit: getNameSchema() },
    onSubmit: ({ value }) => {
      updateName({
        userId: user!.id,
        firstName: value.firstName.trim(),
        lastName: value.lastName.trim()
      });
    }
  });

  if (!user) {
    return null;
  }

  return (
    <form.AppForm>
      <form
        id={FORM_ID}
        className='flex flex-col gap-3'
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppField name='firstName'>{(field) => <field.Input label={t`First name`} />}</form.AppField>
        <form.AppField name='lastName'>{(field) => <field.Input label={t`Last name`} />}</form.AppField>
        <NavioOverlayActions>
          <form.SubscribeButton formId={FORM_ID} disabled={isPending} label={saveLabel ?? t`Save`} />
        </NavioOverlayActions>
      </form>
    </form.AppForm>
  );
};
